import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { colors, font, displayName, generateId, formatCurrency } from "../utils/theme.js";
import { api } from "../api/client.js";
import DetailPanel from "../proposals/DetailPanel.jsx";
import { DEFAULT_NEW_STAGE } from "../proposals/stages.js";
import TextField from "./Field.jsx";
import { currentProjectOf, fmtDate, groupWorkByProject, liveProjectsOf, projectTitle } from "./clientLogic.js";
import ConfirmDialog from "./ConfirmDialog.jsx";
import WorkList from "./WorkList.jsx";
import ClientCrm from "../crm/ClientCrm.jsx";
import MeetingsTab from "../meetings/MeetingsTab.jsx";
import { section, inputStyle, primaryBtn, ghostBtn, editBtn, linkBtn } from "./ui.js";
import TierBadge from "./TierBadge.jsx";

const TABS = ["Overview", "POCs", "Objectives & Tasks", "CRM", "Proposals", "Notes", "Update Meetings"];

// Full-page client detail (increment 5, option B). Sticky identity header + tabbed sections;
// read-first fields; clickable task/proposal rows; optimistic writes with a failure toast.
export default function ClientPage({ clientId, user, users, projects, selectedProjectId = null, onSelectProject, initialTaskId = null, initialCrmTrackId = null, onBack, onChanged }) {
  const [client, setClient] = useState(null);
  const [objectives, setObjectives] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(initialCrmTrackId ? "CRM" : "Objectives & Tasks");
  // A CRM deep link (#client=…&crm=…) opens its track once; leaving and returning to the tab
  // must not reopen it. A new link (hash change) opens the CRM tab again.
  const [crmLink, setCrmLink] = useState(initialCrmTrackId);
  useEffect(() => { if (initialCrmTrackId) { setCrmLink(initialCrmTrackId); setTab("CRM"); } }, [initialCrmTrackId]);
  const [toast, setToast] = useState(null);

  const flash = (type, text) => { setToast({ type, text }); setTimeout(() => setToast((t) => (t && t.text === text ? null : t)), 5000); };
  const admins = useMemo(() => (users || []).filter((u) => u.role === "admin"), [users]);
  const uName = useCallback((id) => { if (!id) return "—"; const x = (users || []).find((y) => y.id === id || y.email === id); return x ? displayName(x) : id; }, [users]);
  // The project this page is about: the one picked on the Clients card / switcher, else the
  // primary. Clients with 2+ live projects get a switcher, and the header counts that project only.
  const current = useMemo(() => currentProjectOf(projects, selectedProjectId), [projects, selectedProjectId]);
  const liveProjects = useMemo(() => liveProjectsOf(projects), [projects]);
  const multi = liveProjects.length > 1;

  const load = useCallback(async () => {
    try {
      const [c, obj, tk, allProps, nt] = await Promise.all([
        api.clients.get(clientId), api.objectives.list({ clientId }), api.tasks.list({ clientId }),
        api.proposals.list(), api.clients.notes.list(clientId),
      ]);
      setClient(c); setObjectives(obj?.items || []); setTasks(tk?.items || []);
      setProposals((allProps || []).filter((p) => p.clientId === clientId));
      setNotes(Array.isArray(nt) ? nt : []);
    } finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  // Deep link (#client=…&task=…): focus the linked task's project once tasks are loaded.
  const linkedRef = useRef(false);
  useEffect(() => {
    if (!initialTaskId || linkedRef.current || !tasks.length) return;
    linkedRef.current = true;
    const t = tasks.find((x) => x.id === initialTaskId);
    if (t?.projectId && t.projectId !== current?.id && liveProjects.some((p) => p.id === t.projectId)) onSelectProject?.(t.projectId);
  }, [initialTaskId, tasks, current, liveProjects, onSelectProject]);

  // Persist client detail fields optimistically (description / sharepointUrl / pocs).
  const saveClient = async (fields) => {
    const prev = client;
    setClient((c) => ({ ...c, ...fields }));
    const res = await api.clients.update(clientId, fields);
    if (res.ok) { setClient(res.data); onChanged?.(); return true; }
    setClient(prev); flash("error", res.data?.error || "Couldn’t save — reverted."); return false;
  };
  const reloadClient = async () => { try { const c = await api.clients.get(clientId); setClient(c); onChanged?.(); } catch { /* ignore */ } };

  const today = new Date().toISOString().slice(0, 10);
  const work = groupWorkByProject(projects, objectives, tasks, current?.id || null);
  const statObjectives = multi ? work.current.objectives : objectives;
  const openTasks = (multi ? work.current.tasks : tasks).filter((t) => t.status !== "Completed");
  const pastDue = openTasks.filter((t) => t.dueDate && t.dueDate.slice(0, 10) < today).length;
  const objDone = statObjectives.filter((o) => o.status === "Completed").length;

  if (loading || !client) return <div style={{ padding: 40, color: colors.textMuted }}>Loading client…</div>;

  return (
    <div>
      <div style={{ fontSize: 12, color: colors.textDim, marginBottom: 10 }}>
        <button onClick={onBack} style={{ ...linkBtn, color: colors.textMuted }}>Clients</button> <span>/ {client.name}</span>
      </div>

      {/* Sticky identity header */}
      <div style={{ position: "sticky", top: 0, zIndex: 5, background: colors.bg, paddingBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, borderBottom: `1px solid ${colors.border}`, paddingBottom: 14 }}>
          {client.logoUrl ? (
            <img src={client.logoUrl} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }}
              style={{ width: 46, height: 46, borderRadius: 10, objectFit: "cover", flexShrink: 0, background: colors.surfaceAlt, border: `1px solid ${colors.border}` }} />
          ) : (
            <div style={{ width: 46, height: 46, borderRadius: 10, background: "linear-gradient(135deg,#1d4ed8,#0ea5e9)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 18, color: "#fff", flexShrink: 0 }}>
              {client.name.slice(0, 2).toUpperCase()}
            </div>
          )}
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: -0.3, color: colors.text }}>{client.name}</h1>
              {(client.archivedAt || (current == null && projects.length > 0)) && <span style={{ fontSize: 10, fontWeight: 700, color: colors.textDim, border: `1px solid ${colors.border}`, borderRadius: 4, padding: "1px 6px" }}>ARCHIVED</span>}
            </div>
            {multi && (
              <div role="group" aria-label="Project" style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
                {liveProjects.map((p) => {
                  const on = p.id === current?.id;
                  return (
                    <button key={p.id} type="button" aria-pressed={on} onClick={() => onSelectProject?.(p.id)} style={{
                      fontFamily: font, fontSize: 12.5, fontWeight: 600, padding: "6px 12px", borderRadius: 8, cursor: "pointer",
                      border: `1px solid ${on ? colors.accent : colors.border}`, background: on ? colors.accentDim : colors.surface, color: on ? colors.accent : colors.textMuted,
                    }}>{projectTitle(p)}{p.startDate > today && <span style={{ color: colors.success, fontWeight: 500 }}> · upcoming</span>}</button>
                  );
                })}
              </div>
            )}
            <div style={{ display: "flex", gap: 22, marginTop: 8, flexWrap: "wrap" }}>
              <Stat k="Project" v={current ? projectTitle(current) : "—"} />
              <Stat k="Dates" v={current ? `${fmtDate(current.startDate)} – ${fmtDate(current.endDate)}` : "—"} />
              <Stat k="Owner" v={uName(current?.accountOwner ?? current?.projectOversight)} />
              <Stat k="PM" v={uName(current?.projectManager)} />
              <Stat k="Tier" v={client.tier ? <TierBadge tier={client.tier} /> : "—"} />
              <Stat k="Objectives" v={`${objDone} of ${statObjectives.length}`} />
              <Stat k="Open tasks" v={<>{openTasks.length}{pastDue > 0 && <span style={{ color: colors.danger }}> · {pastDue} past due</span>}</>} />
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 2, borderBottom: `1px solid ${colors.border}`, marginTop: 12 }}>
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} style={{
              fontSize: 13, color: t === tab ? colors.text : colors.textMuted, fontWeight: t === tab ? 600 : 400,
              padding: "9px 13px", cursor: "pointer", background: "none", border: "none", fontFamily: font,
              borderBottom: `2px solid ${t === tab ? colors.accent : "transparent"}`,
            }}>{t}</button>
          ))}
        </div>
      </div>

      <div style={{ paddingTop: 16 }}>
        {tab === "Overview" && <Overview client={client} current={current} projects={projects} onSelectProject={onSelectProject} uName={uName} saveClient={saveClient} clientId={clientId} reloadClient={reloadClient} flash={flash} />}
        {tab === "POCs" && <Pocs client={client} saveClient={saveClient} />}
        {tab === "Objectives & Tasks" && (
          <WorkList clientId={clientId} current={current} work={work} initialTaskId={initialTaskId} users={users} objectives={objectives} tasks={tasks}
            setObjectives={setObjectives} setTasks={setTasks} clientName={client.name} onChanged={onChanged} flash={flash} reload={load} />
        )}
        {tab === "CRM" && <ClientCrm clientId={clientId} clientName={client.name} users={users} flash={flash} initialTrackId={crmLink} onInitialTrackUsed={() => setCrmLink(null)} />}
        {tab === "Proposals" && (
          <Proposals clientId={clientId} client={client} proposals={proposals} users={users} admins={admins} user={user} reload={load} flash={flash} />
        )}
        {tab === "Notes" && <Notes clientId={clientId} notes={notes} setNotes={setNotes} uName={uName} flash={flash} reload={load} onChanged={onChanged} />}
        {tab === "Update Meetings" && (
          <MeetingsTab clientId={clientId} clientName={client.name} users={users} current={current} projects={multi ? projects : null}
            objectives={objectives} tasks={tasks} proposals={proposals} flash={flash} />
        )}
      </div>

      {toast && (
        <div onClick={() => setToast(null)} style={{ position: "fixed", bottom: 24, right: 24, zIndex: 1200, maxWidth: 360, padding: "12px 16px", borderRadius: 10, fontSize: 13, fontWeight: 600, color: "#fff", background: toast.type === "error" ? colors.danger : colors.success, boxShadow: "0 8px 24px rgba(0,0,0,0.4)" }}>{toast.text}</div>
      )}
    </div>
  );
}

function Stat({ k, v }) {
  return <div><div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: colors.textDim }}>{k}</div><div style={{ fontSize: 13, color: colors.text, marginTop: 1 }}>{v || "—"}</div></div>;
}

// ── Overview tab ──
function Overview({ client, current, projects, onSelectProject, uName, saveClient, clientId, reloadClient, flash }) {
  const [showArchived, setShowArchived] = useState(false);
  const live = liveProjectsOf(projects);
  const archived = (projects || []).filter((p) => p.archivedAt);
  const today = new Date().toISOString().slice(0, 10);
  const statusOf = (p) => (p.archivedAt ? "Archived" : p.startDate > today ? "Upcoming" : p.endDate && p.endDate < today ? "Ended" : "Active");
  const row = (p) => {
    const on = p.id === current?.id;
    return (
      <div key={p.id} style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 12, padding: "10px 0", borderBottom: `1px solid ${colors.border}`, fontSize: 13, color: colors.text, background: on ? colors.accentDim : "transparent" }}>
        {p.archivedAt || on ? <span style={{ fontWeight: 600 }}>{projectTitle(p)}</span>
          : <button type="button" onClick={() => onSelectProject?.(p.id)} style={{ ...linkBtn, fontWeight: 600, textAlign: "left", fontSize: 13 }}>{projectTitle(p)}</button>}
        <span style={{ color: colors.textMuted }}>{fmtDate(p.startDate)} – {fmtDate(p.endDate)}</span>
        <span>{uName(p.accountOwner ?? p.projectOversight)}</span>
        <span>{uName(p.projectManager)}</span>
        <span style={{ color: statusOf(p) === "Upcoming" ? colors.success : colors.textMuted }}>{statusOf(p)}</span>
      </div>
    );
  };
  return (
    <div style={section}>
      <TextField label="Description" value={client.description} multiline placeholder="What this client does…" onSave={(v) => saveClient({ description: v })} />
      <TextField label="SharePoint folder" value={client.sharepointUrl} isLink placeholder="Link to the Active Clients subfolder" onSave={(v) => saveClient({ sharepointUrl: v })} />
      <LogoUpload client={client} saveClient={saveClient} flash={flash} />
      <ReadRow k="Project" v={current ? projectTitle(current) : "—"} />
      <ReadRow k="Account owner" v={uName(current?.accountOwner ?? current?.projectOversight)} />
      <ReadRow k="Project manager" v={uName(current?.projectManager)} />
      <div style={{ display: "flex", gap: 12, marginTop: 14 }}>
        <Contracts clientId={clientId} initial={client.contracts} flash={flash} />
      </div>
      {(projects || []).length > 0 && (
        <div style={{ marginTop: 22 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: colors.textDim, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Projects</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 12, padding: "6px 0", borderBottom: `1px solid ${colors.border}`, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, color: colors.textDim }}>
            <span>Project</span><span>Dates</span><span>Owner</span><span>PM</span><span>Status</span>
          </div>
          {live.map(row)}
          {archived.length > 0 && (
            <button type="button" onClick={() => setShowArchived((v) => !v)} aria-expanded={showArchived} style={{ ...linkBtn, color: colors.textMuted, fontSize: 12.5, padding: "10px 0" }}>
              {showArchived ? "▾" : "▸"} {archived.length} archived project{archived.length === 1 ? "" : "s"}
            </button>
          )}
          {showArchived && archived.map(row)}
        </div>
      )}
    </div>
  );
}
function ReadRow({ k, v }) {
  return (
    <div style={{ display: "flex", gap: 12, padding: "11px 0", borderBottom: `1px solid ${colors.border}` }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, color: colors.textDim, width: 150 }}>{k}</div>
      <div style={{ flex: 1, fontSize: 13.5, color: colors.text }}>{v || "—"}</div>
    </div>
  );
}

// Client logo: drop or pick an image, stored inline as a data URI on the client record
// (same approach as team headshots) — no Blob round-trip needed for a small logo. Scaled
// to fit within 400px and saved as PNG so transparency is preserved.
function LogoUpload({ client, saveClient, flash }) {
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const ingest = (file) => {
    if (!file) return;
    if (!file.type || !file.type.startsWith("image/")) { flash("error", "Please choose an image file (PNG, JPG…)."); return; }
    if (file.size > 5000000) { flash("error", "Image must be under 5MB."); return; }
    setBusy(true);
    const img = new Image();
    img.onload = async () => {
      const max = 400;
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      const dataUri = canvas.toDataURL("image/png");
      URL.revokeObjectURL(img.src);
      try { await saveClient({ logoUrl: dataUri }); }
      catch { flash("error", "Couldn’t save the logo."); }
      finally { setBusy(false); }
    };
    img.onerror = () => { setBusy(false); URL.revokeObjectURL(img.src); flash("error", "Couldn’t read that image."); };
    img.src = URL.createObjectURL(file);
  };

  return (
    <div style={{ display: "flex", gap: 12, padding: "11px 0", borderBottom: `1px solid ${colors.border}` }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, color: colors.textDim, width: 150 }}>Logo</div>
      <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 14 }}>
        {client.logoUrl
          ? <img src={client.logoUrl} alt="" style={{ width: 46, height: 46, borderRadius: 10, objectFit: "contain", background: colors.surfaceAlt, border: `1px solid ${colors.border}`, flexShrink: 0 }} />
          : <div style={{ width: 46, height: 46, borderRadius: 10, background: colors.surfaceAlt, border: `1px dashed ${colors.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: colors.textDim, flexShrink: 0 }}>none</div>}
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); ingest(e.dataTransfer.files?.[0]); }}
          style={{
            flex: 1, cursor: "pointer", padding: "12px 14px", borderRadius: 8, fontSize: 12.5,
            color: colors.textMuted, textAlign: "center",
            border: `1px dashed ${drag ? colors.accent : colors.borderLight}`,
            background: drag ? colors.accentDim : "transparent",
          }}
        >
          {busy ? "Uploading…" : <>Drop an image here, or <span style={{ color: colors.accent }}>browse</span>{client.logoUrl ? " to replace" : ""}</>}
          <input ref={inputRef} type="file" accept="image/*" onChange={(e) => ingest(e.target.files?.[0])} style={{ display: "none" }} />
        </div>
        {client.logoUrl && (
          <button onClick={() => saveClient({ logoUrl: "" })} style={{ background: "transparent", border: "none", color: colors.danger, fontSize: 12, cursor: "pointer", fontFamily: font, whiteSpace: "nowrap" }}>Remove</button>
        )}
      </div>
    </div>
  );
}

// A contract PDF slot: drop or pick a PDF, upload raw bytes to the private Blob container;
// open via a 15-min SAS.
// Up to 5 contract PDFs, newest first, each showing when and by whom it was added.
const MAX_CONTRACTS = 5;
const addedOn = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
function Contracts({ clientId, initial, flash }) {
  const [list, setList] = useState(Array.isArray(initial) ? initial : []);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const inputRef = useRef(null);
  const sorted = [...list].sort((a, b) => (a.uploadedAt < b.uploadedAt ? 1 : -1));
  const full = list.length >= MAX_CONTRACTS;
  const open = async (c) => {
    try { const { url } = await api.clients.contracts.sas(clientId, c.id); if (url) window.open(url, "_blank", "noopener"); }
    catch { flash("error", "Couldn’t open that contract — try again."); }
  };
  const ingest = async (file) => {
    if (!file || busy || full) return;
    if (file.type ? file.type !== "application/pdf" : !/\.pdf$/i.test(file.name || "")) { flash("error", "Please choose a PDF."); return; }
    setBusy(true);
    const res = await api.clients.contracts.upload(clientId, file);
    setBusy(false);
    if (res.ok) setList(res.data.contracts || [...list, res.data.contract]);
    else flash("error", res.data?.error || "Upload failed.");
  };
  const remove = async () => {
    const c = confirming; setConfirming(null);
    const res = await api.clients.contracts.remove(clientId, c.id);
    if (res.ok) setList(res.data?.contracts || list.filter((x) => x.id !== c.id));
    else flash("error", res.data?.error || "Couldn’t remove that contract.");
  };
  return (
    <div style={{ flex: 1, border: `1px solid ${colors.border}`, borderRadius: 8, padding: "10px 12px" }}>
      <div style={{ fontSize: 12, color: colors.text, fontWeight: 600, marginBottom: 6 }}>Contracts</div>
      {sorted.length === 0 && <div style={{ fontSize: 12, color: colors.textDim, marginBottom: 8 }}>None uploaded</div>}
      {sorted.map((c) => (
        <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: `1px solid ${colors.border}`, fontSize: 12.5 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div data-testid="contract-name" style={{ color: colors.text, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</div>
            <div style={{ fontSize: 11.5, color: colors.textMuted }}>Added {addedOn(c.uploadedAt)}{c.uploadedByName ? ` by ${c.uploadedByName}` : ""}</div>
          </div>
          <button type="button" onClick={() => open(c)} style={linkBtn}>Open ↗</button>
          <button type="button" aria-label={`Remove ${c.name}`} onClick={() => setConfirming(c)} style={{ ...linkBtn, color: colors.textDim }}>Remove</button>
        </div>
      ))}
      {full ? (
        <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 8 }}>Limit of {MAX_CONTRACTS} reached — remove one to add another.</div>
      ) : (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); ingest(e.dataTransfer.files?.[0]); }}
          style={{ cursor: "pointer", marginTop: 8, padding: "12px 14px", borderRadius: 8, fontSize: 12.5, color: colors.textMuted, textAlign: "center", border: `1px dashed ${drag ? colors.accent : colors.borderLight}`, background: drag ? colors.accentDim : "transparent" }}
        >
          {busy ? "Uploading…" : <>Add a contract ({list.length} of {MAX_CONTRACTS}) — drop a PDF here, or <span style={{ color: colors.accent }}>browse</span></>}
          <input ref={inputRef} data-kind="contract" type="file" accept="application/pdf" style={{ display: "none" }}
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; ingest(f); }} />
        </div>
      )}
      {confirming && (
        <ConfirmDialog title="Remove contract?" confirmLabel="Remove contract" onCancel={() => setConfirming(null)} onConfirm={remove}>
          “{confirming.name}” will be deleted from this client. This can’t be undone.
        </ConfirmDialog>
      )}
    </div>
  );
}

// ── POCs tab ──
function Pocs({ client, saveClient }) {
  const [pocs, setPocs] = useState(client.pocs || []);
  const [editIdx, setEditIdx] = useState(null);
  const commit = async (next) => { setPocs(next); await saveClient({ pocs: next }); };
  const addNew = () => { setPocs((p) => [...p, { id: generateId(), name: "", email: "", phone: "", calendarLink: "" }]); setEditIdx(pocs.length); };
  return (
    <div style={section}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: colors.textDim, letterSpacing: 1, textTransform: "uppercase" }}>Company POCs</div>
        <button onClick={addNew} style={editBtn}>+ Add contact</button>
      </div>
      {pocs.length === 0 && <div style={{ color: colors.textDim, fontSize: 13 }}>No contacts yet.</div>}
      {pocs.map((p, i) => editIdx === i ? (
        <div key={p.id} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, padding: "10px 0", borderBottom: `1px solid ${colors.border}` }}>
          {["name", "title", "email", "phone", "calendarLink"].map((f) => (
            <input key={f} style={inputStyle} value={p[f] || ""} placeholder={f === "calendarLink" ? "Calendar link" : f[0].toUpperCase() + f.slice(1)}
              onChange={(e) => setPocs((prev) => prev.map((x, idx) => (idx === i ? { ...x, [f]: e.target.value } : x)))} />
          ))}
          <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8 }}>
            <button onClick={() => { commit(pocs.filter((x) => x.name || x.email)); setEditIdx(null); }} style={primaryBtn}>Save</button>
            <button onClick={() => { commit(pocs.filter((x, idx) => x.name || x.email || idx !== i)); setEditIdx(null); }} style={ghostBtn}>Remove</button>
          </div>
        </div>
      ) : (
        <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, padding: "11px 0", borderBottom: `1px solid ${colors.border}` }}>
          <div><div style={{ fontSize: 14, color: colors.text, fontWeight: 600 }}>{p.name || "(unnamed)"}{p.title && <span style={{ fontSize: 12, color: colors.textDim, fontWeight: 400 }}> · {p.title}</span>}</div>
            <div style={{ fontSize: 12, color: colors.textMuted }}>{[p.email, p.phone].filter(Boolean).join(" · ")}{p.calendarLink && <> · <a href={p.calendarLink} target="_blank" rel="noreferrer" style={{ color: colors.accent }}>Calendar ↗</a></>}</div>
          </div>
          <button onClick={() => setEditIdx(i)} style={editBtn}>Edit</button>
        </div>
      ))}
    </div>
  );
}

// ── Proposals tab ──
function Proposals({ clientId, client, proposals, users, admins, user, reload, flash }) {
  const [editing, setEditing] = useState(null); // proposal object or "new"
  const isNew = editing === "new";
  const createProposal = async (record) => {
    const now = new Date().toISOString();
    const stage = record.stage || DEFAULT_NEW_STAGE;
    const doc = { ...record, id: generateId(), clientId, stage, stageHistory: [{ stage, enteredAt: now, changedBy: user?.email || "system", override: false }], createdAt: now, updatedAt: now };
    try { await api.proposals.save(doc); setEditing(null); reload(); return { ok: true }; }
    catch (e) { return { ok: false, error: e.message }; }
  };
  // Re-point the open panel at the current server copy (fresh _etag) so a retry after a
  // conflict uses the right version. Matches by id; also refreshes the list.
  const refreshEditing = async () => {
    try {
      const list = await api.proposals.list();
      const fresh = (list || []).find((p) => p.id === editing.id);
      if (fresh) setEditing(fresh);
    } catch { /* ignore — reload() below still runs */ }
    reload();
  };
  const saveFields = async (fields) => {
    const res = await api.proposals.updateFields(editing.id, fields, editing._etag);
    // On success the server returns the updated doc (with the new _etag) — adopt it so the
    // NEXT edit on this still-open panel doesn't 409 against its own previous save.
    if (res.ok) { setEditing(res.data); reload(); return { ok: true }; }
    if (res.status === 409) { await refreshEditing(); return { ok: false, error: "That proposal changed — refreshed to the latest. Review and save again." }; }
    return { ok: false, error: res.data?.error || "Save failed." };
  };
  const setStage = async (toStage) => {
    const res = await api.proposals.setStage(editing.id, toStage, editing._etag, user?.email);
    if (res.ok) { setEditing(res.data); reload(); return { ok: true }; }
    if (res.status === 409) { await refreshEditing(); return { ok: false, error: "That proposal changed — refreshed to the latest. Try the change again." }; }
    return { ok: false, error: res.data?.error || "Stage change failed." };
  };
  return (
    <div style={section}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: colors.textDim, letterSpacing: 1, textTransform: "uppercase" }}>Proposals</div>
        <button onClick={() => setEditing("new")} style={editBtn}>+ Add proposal</button>
      </div>
      {proposals.length === 0 && <div style={{ color: colors.textDim, fontSize: 13 }}>No proposals for this client.</div>}
      {proposals.map((p) => (
        <div key={p.id} onClick={() => setEditing(p)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: 7, cursor: "pointer", borderBottom: `1px solid ${colors.border}` }}
          onMouseEnter={(e) => (e.currentTarget.style.background = colors.surfaceAlt)} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
          <span style={{ flex: 1, fontSize: 13, color: colors.text }}>{p.topic || "(untitled)"}</span>
          <span style={{ fontSize: 12, color: colors.textDim }}>{p.stage}{Number(p.value) > 0 ? ` · ${formatCurrency(p.value)}` : ""}</span>
          <span style={{ color: colors.textDim }}>›</span>
        </div>
      ))}
      {editing && (
        <DetailPanel proposal={isNew ? null : editing} isNew={isNew} presetClientId={clientId}
          users={users} admins={admins} clients={[client]}
          onCreate={createProposal} onSaveFields={saveFields} onSetStage={setStage} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

// ── Notes tab ──
function Notes({ clientId, notes, setNotes, uName, flash, reload, onChanged }) {
  const [text, setText] = useState("");
  const [filter, setFilter] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");

  const post = async () => {
    const t = text.trim(); if (!t) return;
    setText("");
    const tempId = "temp-" + Date.now();
    const optimistic = { id: tempId, clientId, text: t, author: "me", authorName: "You", createdAt: new Date().toISOString(), _pending: true };
    setNotes((n) => [...n, optimistic]);
    const res = await api.clients.notes.create(clientId, t);
    if (res.ok) { setNotes((n) => n.map((x) => (x.id === tempId ? res.data : x))); onChanged?.(); }
    else { setNotes((n) => n.filter((x) => x.id !== tempId)); flash("error", "Couldn’t post note."); }
  };
  const saveEdit = async () => {
    const t = editText.trim(); if (!t) return;
    const id = editId; setEditId(null);
    const prev = notes;
    setNotes((n) => n.map((x) => (x.id === id ? { ...x, text: t, editedAt: new Date().toISOString() } : x)));
    const res = await api.clients.notes.update(id, t);
    if (res.ok) setNotes((n) => n.map((x) => (x.id === id ? res.data : x)));
    else { setNotes(prev); flash("error", "Couldn’t save edit."); }
  };

  const sorted = [...notes].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)); // newest first
  const q = filter.trim().toLowerCase();
  const filtered = q ? sorted.filter((n) => (n.text || "").toLowerCase().includes(q)) : sorted;
  const shown = showAll || q ? filtered : filtered.slice(0, 5);

  return (
    <div style={section}>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {/* Enter adds a line; Cmd/Ctrl+Enter (or Post) submits. */}
        <textarea rows={3} style={{ ...inputStyle, resize: "vertical", fontFamily: font, lineHeight: 1.45 }} value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); post(); } }}
          placeholder="Add a note… (⌘/Ctrl+Enter to post)" />
        <button onClick={post} style={{ ...primaryBtn, alignSelf: "flex-start" }}>Post</button>
      </div>
      {notes.length > 0 && <input style={{ ...inputStyle, marginBottom: 10 }} value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search notes…" />}
      {shown.length === 0 && <div style={{ color: colors.textDim, fontSize: 13 }}>{q ? "No notes match." : "No notes yet."}</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {shown.map((n) => (
          <div key={n.id} style={{ borderLeft: `2px solid ${colors.border}`, paddingLeft: 12, opacity: n._pending ? 0.6 : 1 }}>
            {editId === n.id ? (
              <div style={{ display: "flex", gap: 8 }}>
                <textarea rows={Math.min(10, Math.max(3, editText.split("\n").length))} style={{ ...inputStyle, resize: "vertical", fontFamily: font, lineHeight: 1.45 }} value={editText} autoFocus onChange={(e) => setEditText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); saveEdit(); } if (e.key === "Escape") setEditId(null); }} />
                <button onClick={saveEdit} style={{ ...linkBtn, alignSelf: "flex-start" }}>Save</button>
                <button onClick={() => setEditId(null)} style={{ ...linkBtn, color: colors.textDim, alignSelf: "flex-start" }}>Cancel</button>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 13, color: colors.text, whiteSpace: "pre-wrap" }}>{n.text}</div>
                <div style={{ fontSize: 11, color: colors.textDim }}>
                  {n.authorName || uName(n.author)} · {new Date(n.createdAt).toLocaleString()}{n.editedAt ? " · edited" : ""}
                  {!n._pending && <button onClick={() => { setEditId(n.id); setEditText(n.text); }} style={{ ...linkBtn, marginLeft: 8 }}>Edit</button>}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
      {!showAll && !q && filtered.length > 5 && <button onClick={() => setShowAll(true)} style={{ ...editBtn, marginTop: 10 }}>Show all {filtered.length} notes</button>}
    </div>
  );
}
